import useBeaconDraggable from 'components/packages/beacon/useBeaconDraggable'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Footer from './footer'
import BeaconImage from './image'
import { FormSection, FutureSection, QuestionnaireSection, TaskSection } from './section'
import QuestionnaireBeaconbackdrop from './backdrop'

const useSliderDotItems = ({ totalItems = 0, gap = 11 }) => {
  const [currentStep, setCurrentStep] = useState(0)
  const [swapPair, setSwapPair] = useState(null)

  const dotsRef = useRef([])

  useEffect(() => {
    if (swapPair) {
      const { oldIndex, newIndex } = swapPair
      const gap = 11

      const oldDot = dotsRef.current[oldIndex]
      const newDot = dotsRef.current[newIndex]

      if (oldDot && newDot) {
        // Remove transitions temporarily
        oldDot.style.transition = 'none'
        newDot.style.transition = 'none'

        // Set initial positions (swapped)
        if (newIndex > oldIndex) {
          oldDot.style.transform = `translateX(${gap}px)`
          newDot.style.transform = `translateX(-${gap}px)`
        } else {
          oldDot.style.transform = `translateX(-${gap}px)`
          newDot.style.transform = `translateX(${gap}px)`
        }

        setTimeout(() => {
          // Re-enable transitions after swapped initial positions
          oldDot.style.transition = 'all 0.5s ease-in-out'
          newDot.style.transition = 'all 0.5s ease-in-out'
          // Reset transforms (will animate back to original positions)
          oldDot.style.transform = 'translateX(0)'
          newDot.style.transform = 'translateX(0)'
          // Clean up after animation
          setSwapPair(null)
        }, 0)
      }
    }
  }, [swapPair])

  const next = useCallback(() => {
    if (currentStep < totalItems - 1) {
      const newStep = currentStep + 1
      setSwapPair({ oldIndex: currentStep, newIndex: newStep })
      setCurrentStep(newStep)
    }
  }, [currentStep, totalItems])

  const prev = useCallback(() => {
    if (currentStep > 0) {
      const newStep = currentStep - 1
      setSwapPair({ oldIndex: currentStep, newIndex: newStep })
      setCurrentStep(newStep)
    }
  }, [currentStep])

  return {
    currentStep,
    dotsRef,
    next,
    prev,
  }
}

const TOTAL_STEPS = 4
const ContentMap = [QuestionnaireSection, FormSection, TaskSection, FutureSection]

const QuestionnaireRequestBeacon = ({ onClose, disableCheckBox }) => {
  const iconDragRef = useRef(null)
  const beaconSliderRef = useRef(null)

  useBeaconDraggable({ beaconSliderRef, iconDragRef })

  const { next, prev, currentStep, dotsRef } = useSliderDotItems({ totalItems: TOTAL_STEPS })

  const isFirstStep = currentStep === 0
  const isLastStep = currentStep === TOTAL_STEPS - 1

  const CurrentContent = ContentMap[currentStep] || QuestionnaireSection

  const bodyLayoutStyle = useMemo(() => {
    let height = 200
    if (isFirstStep) height = 180
    if (isLastStep) height = 121

    return {
      width: 552,
      height: height,
      position: 'relative',
      transition: 'all 0.5s ease-in-out',
    }
  }, [currentStep, isLastStep, isFirstStep])

  const contentLayoutStyle = useMemo(() => {
    let width = 210
    if (isFirstStep) width = 296
    if (isLastStep) width = 552

    return { width, paddingTop: 24 }
  }, [currentStep, isLastStep, isFirstStep])

  const imageVisibilityStyle = useMemo(
    () => ({
      opacity: isLastStep ? 0 : 1,
      height: isLastStep ? 0 : 'auto',
      transition: 'all 0.5s ease-in-out',
      overflow: isLastStep ? 'hidden' : 'visible',
      visibility: isLastStep ? 'hidden' : 'visible',
    }),
    [isLastStep]
  )

  return (
    <div ref={beaconSliderRef} className="questionnaire-beacon-wrapper">
      <QuestionnaireBeaconbackdrop />
      <div className="questionnaire-beacon-body">
        <span style={{ position: 'absolute', cursor: 'grab', left: 15, top: 16 }}>
          <i ref={iconDragRef} className="fa-thin fa-xl fa-grip-dots-vertical dome-color-med-grey"></i>
        </span>
        <span style={{ position: 'absolute', cursor: 'pointer', right: 25, top: 16 }}>
          <i onClick={onClose} className="fa-light fa-xmark dome-color-med-grey"></i>
        </span>
        <div className="dome-d-flex dome-flex-column dome-gap24">
          <div style={bodyLayoutStyle}>
            <div style={{ position: 'absolute', left: 0 }} className="questionnaire-beacon">
              <div style={contentLayoutStyle}>
                <div className="dome-d-flex dome-flex-column dome-gap12">
                  <ul className="dome-list-no-style dome-d-flex dome-gap6 dome-relative">
                    {new Array(TOTAL_STEPS).fill(null).map((e, index) => {
                      return (
                        <li
                          key={index}
                          aria-selected={index === currentStep}
                          ref={(el) => (dotsRef.current[index] = el)}
                          className="questionnaire-beacon-radio-item"
                        />
                      )
                    })}
                  </ul>
                  <CurrentContent />
                </div>
              </div>
            </div>
            <div style={{ position: 'absolute', right: 0, height: '100%' }}>
              <div className="dome-d-flex dome-justify-center dome-align-center dome-full-height">
                <div style={imageVisibilityStyle}>
                  <BeaconImage currentStep={currentStep} />
                </div>
              </div>
            </div>
          </div>
          <Footer
            next={next}
            prev={prev}
            onClose={onClose}
            totalSteps={TOTAL_STEPS}
            currentStep={currentStep}
            disableCheckBox={disableCheckBox}
          />
        </div>
      </div>
    </div>
  )
}

export default QuestionnaireRequestBeacon
